import {
  Button,
  isWeb,
  SizableText,
  useDidFinishSSR,
  useMedia,
  XStack,
  YStack,
} from '@mythical/ui'
import { Video } from 'app/blocks/video'
import useContent from 'app/hooks/use-content'
import useAnalytics from 'app/lib/analytics'
import { Content } from 'app/queries/content/content'
import { format } from 'date-fns'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useRouter } from 'solito/router'

import { RenderBlocks } from 'app/components/blocks/RenderBlocks'
import CommentLock from 'app/components/comments/CommentLock'
import { CommentsList } from 'app/components/comments/CommentsList'
import { Meta } from 'app/components/primitive/Meta'
import Spinner from 'app/components/primitive/Spinner'
import Section from 'app/components/structure/Section'
import ContentInfo from './ContentInfo'
import ContentLockThumbnail from './ContentLockModal'
import RelatedContent from './RelatedContent'
import { ContentVideoArea } from './ContentVideoArea'
import { useStores } from 'app/store'
import EventBatcher from 'app/lib/watch-tracker'
import { useEpisodesList } from 'app/hooks/use-episodes-list'
import {
  ContentEpisodesListHorizontal,
  ContentEpisodesListVertical,
} from './ContentEpisodesList'
import { FullScreenOverlay } from 'app/components/primitive/FullScreenOverlay'
import { ButtonLink } from 'app/components/primitive/Button'
import { IconChevronLeft } from 'app/components/primitive/Icon/icons/IconChevronLeft'
import { IconChevronRight } from 'app/components/primitive/Icon'
interface ContentProps {
  content?: Content
  slug: string
  series?: string
  id?: string
  preview?: boolean
  token?: string
  scrollToTop?: () => void
  commentId?: string
  scrollRef?: any
}

export const ContentView = observer<ContentProps>(
  ({
    content,
    series,
    id,
    slug,
    preview,
    token,
    scrollToTop,
    commentId,
    scrollRef,
  }) => {
    const router = useRouter()
    const media = useMedia()
    const hasMounted = useDidFinishSSR()
    const { userStore } = useStores()
    const analytics = useAnalytics()

    const {
      data: originalData,
      loading,
      error,
      setParams,
      fetchData,
      collectionName,
      collectionId,
      rateContent,
      toggleFavourite,
      toggleWatchLater,
    } = useContent({
      id,
      slug,
      preview,
      token,
    })

    const [data, setData] = useState(content || originalData)

    useEffect(() => {
      if (hasMounted && !originalData?.slug && loading) return
      setData(originalData || content)
    }, [originalData, loading, hasMounted, data?.slug, slug])

    useEffect(() => {
      if (data?.id && userStore) {
        const batcher = EventBatcher.getInstance(userStore)

        batcher.addEvent({
          content: data?.id,
        })
      }
    }, [data?.id, userStore])

    const usePrimaryCollection =
      !series &&
      data?.isInSeries &&
      (!collectionId || data?.primaryCollectionId === collectionId)

    const currentSeries = usePrimaryCollection
      ? data?.primaryCollectionId
      : series

    const handlePress = (slug: string, url: string) => {
      setParams({ slug })
      if (isWeb) {
        router.replace(url, undefined, {
          shallow: true,
          scroll: true,
        })
      }
      scrollToTop?.()
    }

    const episodesList = useEpisodesList(currentSeries, data?.id, handlePress)

    const disableRecommendations = data?.disableRecommendations

    const datePublished =
      data?.createdAt && format(new Date(data.createdAt), 'MMM d, yyyy')

    const nextSlug = currentSeries ? episodesList.nextSlug : data?.next?.slug
    const prevSlug = currentSeries
      ? episodesList.previousSlug
      : data?.previous?.slug

    const nextUrl = currentSeries
      ? `/series/${episodesList.slug}/${nextSlug}`
      : `/content/${nextSlug}`

    const prevUrl = series
      ? `/series/${episodesList.slug}/${prevSlug}`
      : `/content/${prevSlug}`

    const blocks = data?.content || []

    const videoBlock = blocks.find((b) => b.__typename === 'Video') as Video
    const nonVideoBlocks = blocks.filter((b) => b.__typename !== 'Video')
    const disableComments = data?.disableComments

    const hasContent = data?.content && data.content.length > 0
    const isLocked =
      !preview && // Not being previewed by an admin
      !data?.isUnlocked // User does not have access to it

    const showVideo = videoBlock && (preview || data?.isUnlocked)

    if (loading && !data?.content) {
      return (
        <FullScreenOverlay>
          <Spinner size="large" />
        </FullScreenOverlay>
      )
    }

    return (
      <Section paddedX fs={1} fg={1} gap="$3" $gtXs={{ gap: '$5' }}>
        {data && (
          <Meta
            title={data.shareTitle || data.title}
            description={data.shareDescription}
            image={data.image}
            type="content"
            slug={data.slug}
          />
        )}

        {error && (
          <YStack gap="$3">
            <SizableText textTransform="uppercase" size="$2" py="$1.5">
              Something went wrong
            </SizableText>
            <Button onPress={() => fetchData()}>Try Again</Button>
          </YStack>
        )}

        {data && (
          <YStack gap="$3" $gtXs={{ gap: '$5' }}>
            <YStack
              gap="$3"
              $gtXs={{ gap: '$5' }}
              $gtXxl={{ fd: 'row', ai: 'flex-start', gap: '$4' }}
            >
              {isLocked && !showVideo && (
                <ContentLockThumbnail image={data.image2x || data.image} />
              )}

              {videoBlock && showVideo && (
                <ContentVideoArea
                  contentId={data.id}
                  title={data.title}
                  thumbnail={data.image}
                  block={videoBlock}
                  planLevel={data?.contentTier}
                  currentProgress={data?.currentProgress}
                  onVideoPlay={() => {
                    analytics.logEvent('video_play', {
                      content_id: data.id,
                      content_title: data.title,
                      collection_id: data.primaryCollectionId,
                      collection_title: data.category,
                    })
                  }}
                  onVideoPause={() => {
                    analytics.logEvent('video_pause', {
                      content_id: data.id,
                      content_title: data.title,
                      collection_id: data.primaryCollectionId,
                      collection_title: data.category,
                    })
                  }}
                  onVideoEnd={() => {
                    analytics.logEvent('video_complete', {
                      content_id: data.id,
                      content_title: data.title,
                      collection_id: data.primaryCollectionId,
                      collection_title: data.category,
                    })

                    if (currentSeries && nextSlug) {
                      handlePress(nextSlug, nextUrl)
                    }
                  }}
                  onVideoProgress={(progress) => {
                    analytics.logEvent('video_progress', {
                      content_id: data.id,
                      content_title: data.title,
                      collection_id: data.primaryCollectionId,
                      collection_title: data.category,
                      progress,
                    })
                  }}
                />
              )}

              {currentSeries && media.gtXxl && (
                <ContentEpisodesListVertical
                  dsp="none"
                  $gtXxl={{ dsp: 'flex' }}
                  title={episodesList.title}
                  episodes={episodesList.episodes}
                  seasons={episodesList.seasons}
                  season={episodesList.activeSeason}
                  loading={episodesList.loading}
                  onSeasonChange={episodesList.setActiveSeason}
                  onContentClick={episodesList.handleContentClick}
                  currentContentId={data?.id}
                />
              )}
            </YStack>

            <ContentInfo
              title={data.title}
              slug={data.slug}
              category={data.category}
              categoryHref={data.categoryHref}
              datePublished={datePublished}
              commentCount={data.commentCount}
              isVideo={data.videoPost}
              likeCount={data.totalFavourites}
              hasLiked={data.isFavourited}
              onLike={toggleFavourite}
              disableComments={disableComments}
              isLocked={isLocked}
              series={currentSeries && episodesList.slug}
              onRatingPress={rateContent}
              currentRating={data.currentRating}
              isInWatchLater={data.isInWatchLater}
              onToggleWatchLater={toggleWatchLater}
            />

            {hasContent && !isLocked && (
              <RenderBlocks
                blocks={nonVideoBlocks}
                slug={data.slug}
                spaced
                inContent
              />
            )}

            <XStack gap="$3" pt="$2" alignItems="center">
              <ButtonLink
                theme="secondary"
                scaleSpace={0.3}
                href={prevUrl}
                icon={<IconChevronLeft size={22} />}
                $xxs={{ fs: 1, fg: 1, fb: '49%' }}
                disabled={!prevSlug}
                {...(prevSlug && {
                  href: prevUrl,
                  onPressCustom: () => handlePress(prevSlug, prevUrl),
                })}
              >
                Previous
              </ButtonLink>

              <ButtonLink
                theme="secondary"
                scaleSpace={0.3}
                href={nextUrl}
                iconAfter={<IconChevronRight size={22} />}
                $xxs={{ fs: 1, fg: 1, fb: '49%' }}
                disabled={!nextSlug}
                {...(nextSlug && {
                  href: nextUrl,
                  onPressCustom: () => handlePress(nextSlug, nextUrl),
                })}
              >
                Next
              </ButtonLink>
            </XStack>

            <YStack gap="$6" pt="$4" $gtSm={{ pt: '$0' }}>
              {currentSeries && media.xxl && (
                <ContentEpisodesListHorizontal
                  title={episodesList.title}
                  episodes={episodesList.episodes}
                  seasons={episodesList.seasons}
                  season={episodesList.activeSeason}
                  loading={episodesList.loading}
                  onSeasonChange={episodesList.setActiveSeason}
                  onContentClick={episodesList.handleContentClick}
                  currentContentId={data?.id}
                  $gtSm={{ overflow: 'hidden' }}
                  $gtXxl={{ dsp: 'none' }}
                />
              )}

              {!disableRecommendations &&
                !currentSeries &&
                data?.related?.length > 0 && (
                  <RelatedContent
                    title={collectionName || data?.category}
                    items={data?.related || []}
                    onNavigate={() => scrollToTop?.()}
                    isVideo={data.videoPost}
                    $gtSm={{ overflow: 'hidden' }}
                  />
                )}

              {disableComments && (
                <YStack
                  py="$3.5"
                  jc="center"
                  ai="center"
                  bg="$bgSecondary"
                  br="$4"
                  p="$3.5"
                >
                  <SizableText color="$fgSecondary" ta="center" size="$5">
                    Comments have been disabled for this post.
                  </SizableText>
                </YStack>
              )}

              {!disableComments && (
                <>
                  {!isLocked ? (
                    <CommentsList
                      parent={data.id}
                      slug={data.slug}
                      commentCount={data.commentCount}
                      targetCommentId={commentId}
                      scrollRef={scrollRef}
                    />
                  ) : (
                    <CommentLock commentCount={data.commentCount} />
                  )}
                </>
              )}
            </YStack>
          </YStack>
        )}
      </Section>
    )
  },
)
